import { db } from "../lib/firebase";
import { errorDev } from "../utils/logger";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  updateDoc,
  increment,
  deleteDoc,
} from "../lib/firestoreClient";

const PRODUCTS = "products";
const OWNER_FIELD_CANDIDATES = [
  "poster.userId",
  "userId",
  "ownerId",
  "sellerId",
];

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value === "number") return value;
  return new Date(value).getTime() || 0;
}

function mapDoc(docRef) {
  return { id: docRef.id, ...docRef.data() };
}

function sortNewestFirst(rows) {
  return rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
}

function dedupeById(rows) {
  const byId = new Map();
  rows.forEach((row) => {
    if (row?.id && !byId.has(row.id)) byId.set(row.id, row);
  });
  return Array.from(byId.values());
}

export const productRepository = {
  path: PRODUCTS,

  /**
   * Live single-document listener used by the profile page to keep the
   * per-product wish count fresh without the page building the ref itself.
   *
   * @param {string} productId
   * @param {Function} callback receives the mapped row (or null when absent).
   * @param {Function} [onError] optional error handler.
   * @returns {Function} unsubscribe.
   */
  subscribeById(productId, callback, onError = null) {
    if (!productId) {
      callback(null);
      return () => {};
    }
    return onSnapshot(
      doc(db, PRODUCTS, String(productId)),
      (snap) => {
        callback(snap.exists() ? mapDoc(snap) : null);
      },
      (error) => {
        errorDev("productRepository: single-product listener failed", error);
        if (onError) onError(error);
      }
    );
  },

  async getById(productId) {
    if (!productId) return null;
    const ref = doc(db, PRODUCTS, String(productId));
    const snap = await getDoc(ref);
    return snap.exists() ? mapDoc(snap) : null;
  },

  async getByIds(productIds = []) {
    const uniqueIds = Array.from(new Set((productIds || []).filter(Boolean).map(String)));
    const rows = await Promise.all(
      uniqueIds.map(async (productId) => {
        try {
          return await this.getById(productId);
        } catch (_) {
          return null;
        }
      })
    );
    return rows.filter(Boolean);
  },

  async listRecent({ max = 500 } = {}) {
    const safeMax = Math.max(1, Math.min(Number(max) || 500, 500));
    const snap = await getDocs(
      query(collection(db, PRODUCTS), orderBy("createdAt", "desc"), limit(safeMax))
    );
    return snap.docs.map(mapDoc);
  },

  async listByOwner(userId, { max = 500, ownerFields = OWNER_FIELD_CANDIDATES } = {}) {
    if (!userId) return [];
    const safeMax = Math.max(1, Math.min(Number(max) || 500, 500));
    const snapshots = await Promise.all(
      ownerFields.map((field) =>
        getDocs(query(collection(db, PRODUCTS), where(field, "==", String(userId)), limit(safeMax)))
          .catch(() => ({ docs: [] }))
      )
    );

    const rows = snapshots.flatMap((snap) => snap.docs.map(mapDoc));
    return sortNewestFirst(dedupeById(rows));
  },

  /**
   * Live ownership listener. The four owner fields are queried in parallel and
   * merged by id, mirroring `listByOwner`, so a product written through any of
   * the historical owner fields is visible the moment it changes.
   *
   * @param {string} userId owner id.
   * @param {Function} callback receives the merged, filtered, newest-first rows.
   * @param {Function} [onError] optional error handler.
   * @returns {Function} unsubscribe.
   */
  subscribeByOwner(userId, callback, onError = null) {
    if (!userId) {
      callback([]);
      return () => {};
    }

    const buckets = {};
    const pendingKeys = new Set(OWNER_FIELD_CANDIDATES);

    const emit = () => {
      const merged = new Map();
      Object.values(buckets).forEach((list) => {
        list.forEach((item) => {
          if (item?.id) merged.set(item.id, item);
        });
      });
      const rows = sortNewestFirst(
        Array.from(merged.values()).filter((item) => {
          const status = String(item?.status || "").toLowerCase();
          return !item?.isDeleted && status !== "deleted" && status !== "archived";
        })
      );
      callback(rows);
    };

    const markDone = (key) => {
      pendingKeys.delete(key);
      if (pendingKeys.size === 0 && typeof onError === "function") {
        // All sources settled; no error to report.
      }
    };

    const unsubscribers = OWNER_FIELD_CANDIDATES.map((field) => {
      const q = query(collection(db, PRODUCTS), where(field, "==", String(userId)));
      return onSnapshot(
        q,
        (snap) => {
          buckets[field] = (snap?.docs || []).map(mapDoc);
          emit();
          markDone(field);
        },
        (error) => {
          errorDev("productRepository: ownership listener failed on", field, error);
          markDone(field);
          if (onError) onError(error, field);
        }
      );
    }).filter((unsub) => typeof unsub === "function");

    return () => unsubscribers.forEach((unsub) => unsub());
  },

  /**
   * Best-effort wish-count bump. The caller toggles the wish flag and the count
   * follows; a failed count write must not block the wishlist toggle.
   *
   * @param {string} productId
   * @param {number} delta +1 / -1
   * @returns {Promise<void>}
   */
  async incrementWishCount(productId, delta) {
    if (!productId || !delta) return;
    try {
      await updateDoc(doc(db, PRODUCTS, String(productId)), {
        wishCount: increment(Number(delta) || 0),
        updatedAt: new Date(),
      });
    } catch (err) {
      // Best-effort: the wish flag is the source of truth for the UI.
      errorDev("productRepository: wishCount update failed", err);
    }
  },

  /**
   * Owner visibility toggle. Mirrors the three flag aliases the UI checks.
   *
   * @param {string} productId
   * @param {boolean} nextHidden
   * @returns {Promise<void>}
   */
  async updateVisibility(productId, nextHidden) {
    if (!productId) return;
    await updateDoc(doc(db, PRODUCTS, String(productId)), {
      hidden: nextHidden,
      isHidden: nextHidden,
      isPrivate: nextHidden,
      updatedAt: new Date(),
    });
  },

  /**
   * Owner-only delete. The page relies on Firestore rules to refuse the
   * operation for non-owners, so the repository does not duplicate that check.
   *
   * @param {string} productId
   * @returns {Promise<void>}
   */
  async delete(productId) {
    if (!productId) return;
    await deleteDoc(doc(db, PRODUCTS, String(productId)));
  },
};

export default productRepository;
